import { writable, derived } from 'svelte/store';

// Search state store
function createSearchStore() {
    // Core state
    const results = writable([]);
    const currentIndex = writable(-1);
    const lastOptions = writable(null);
    const isSearching = writable(false);
    
    // Derived stores for convenience
    const currentResult = derived(
        [results, currentIndex],
        ([$results, $currentIndex]) => {
            if ($currentIndex >= 0 && $currentIndex < $results.length) {
                return $results[$currentIndex];
            }
            return null;
        }
    );
    
    const hasResults = derived(
        results,
        $results => $results.length > 0
    );
    
    const resultCount = derived(
        results,
        $results => $results.length
    );
    
    const navigationInfo = derived(
        [currentIndex, resultCount],
        ([$currentIndex, $resultCount]) => ({
            current: $currentIndex + 1,
            total: $resultCount,
            canGoNext: $resultCount > 0,
            canGoPrev: $resultCount > 0
        })
    );
    
    // Actions
    function setResults(searchResults) {
        results.set(searchResults);
        currentIndex.set(searchResults.length > 0 ? 0 : -1);
    }
    
    function clearSearch() {
        results.set([]);
        currentIndex.set(-1);
        lastOptions.set(null);
        isSearching.set(false);
    }
    
    function navigateNext() {
        currentIndex.update(index => {
            const $results = get(results);
            if ($results.length === 0) return -1;
            return (index + 1) % $results.length;
        });
    }
    
    function navigatePrev() {
        currentIndex.update(index => {
            const $results = get(results);
            if ($results.length === 0) return -1;
            return (index - 1 + $results.length) % $results.length;
        });
    }
    
    function setCurrentIndex(index) {
        currentIndex.set(index);
    }
    
    function setLastOptions(options) {
        lastOptions.set(options);
    }
    
    function setIsSearching(searching) {
        isSearching.set(searching);
    }
    
    // Update results with current highlighting
    function updateResultsWithCurrent() {
        results.update($results => {
            const $currentIndex = get(currentIndex);
            return $results.map((r, i) => ({
                ...r,
                isCurrent: i === $currentIndex
            }));
        });
    }
    
    return {
        // State stores
        results: { subscribe: results.subscribe },
        currentIndex: { subscribe: currentIndex.subscribe },
        lastOptions: { subscribe: lastOptions.subscribe },
        isSearching: { subscribe: isSearching.subscribe },
        
        // Derived stores
        currentResult: { subscribe: currentResult.subscribe },
        hasResults: { subscribe: hasResults.subscribe },
        resultCount: { subscribe: resultCount.subscribe },
        navigationInfo: { subscribe: navigationInfo.subscribe },
        
        // Actions
        setResults,
        clearSearch,
        navigateNext,
        navigatePrev,
        setCurrentIndex,
        setLastOptions,
        setIsSearching,
        updateResultsWithCurrent
    };
}

// Helper to get store value imperatively (for use in services)
function get(store) {
    let value;
    store.subscribe(v => value = v)();
    return value;
}

export const searchStore = createSearchStore();